// Persistent top banner shown while the main app is running under an operator
// impersonation session (seeded from the #impersonate=<jwt> handoff fragment).
// Renders the impersonated user's email + the mode, and an Exit control that
// clears the in-memory token and returns to the operator portal (or /login when
// no portal origin is configured).

import { config } from "@/lib/config";
import {
  exitImpersonation,
  getImpersonationSession,
  type ImpersonationSession,
} from "@/lib/auth/impersonationHandoff";

function modeLabel(mode: ImpersonationSession["mode"]): string {
  return mode === "act_as" ? "act-as" : "view-only";
}

export function ImpersonationBanner() {
  const session = getImpersonationSession();
  if (!session) return null;

  function onExit() {
    exitImpersonation();
    // Return the operator to the portal if configured, else to the local login.
    // Either way the impersonation token is already cleared from memory above.
    const dest = config.adminPortalUrl || "/login";
    window.location.assign(dest);
  }

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: "8px 16px",
        background: session.mode === "act_as" ? "#7f1d1d" : "#92400e",
        color: "#fff",
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      <span>
        Impersonating <strong>{session.email}</strong> ({modeLabel(session.mode)})
      </span>
      <button
        type="button"
        onClick={onExit}
        data-testid="impersonation-exit"
        style={{
          background: "rgba(255,255,255,0.18)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 4,
          padding: "2px 10px",
          cursor: "pointer",
          fontSize: 12,
        }}
      >
        Exit
      </button>
    </div>
  );
}
